import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Clock, Ticket, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ticketsApi } from '../lib/api';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  // Mercado Pago returns collection_status for payment status
  const collectionStatus = searchParams.get('collection_status');
  const paymentId = searchParams.get('payment_id');
  // Get the raw status from URL - we'll determine final status after fetching order
  const urlStatus = searchParams.get('status');
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [finalStatus, setFinalStatus] = useState('pending');

  useEffect(() => {
    const verifyAndFetchOrder = async () => {
      if (orderId) {
        try {
          // First, fetch the order to check its current status and if it's free
          const orderResponse = await ticketsApi.getOrder(orderId);
          const orderData = orderResponse.data;
          setOrder(orderData);
          
          // Determine the final status based on order data
          // For FREE orders (total_amount = 0), they're immediately approved
          if (orderData.total_amount === 0 && orderData.status === 'approved') {
            setFinalStatus('success');
            setLoading(false);
            return;
          }
          
          // If order is already approved/completed, show success
          if (orderData.status === 'approved' || orderData.status === 'completed') {
            setFinalStatus('success');
            setLoading(false);
            return;
          }
          
          // If order is rejected/cancelled, show that status
          if (orderData.status === 'rejected' || orderData.status === 'cancelled') {
            setFinalStatus(orderData.status);
            setLoading(false);
            return;
          }
          
          // For pending orders with MercadoPago payment
          // Check if user abandoned (came back without paying - no collection_status and no payment_id)
          const wasAbandoned = (collectionStatus === 'null' || collectionStatus === null || collectionStatus === '') 
                              && !paymentId 
                              && orderData.status === 'pending'
                              && orderData.total_amount > 0;
          
          if (wasAbandoned) {
            // Cancel the abandoned order
            await ticketsApi.cancelOrder(orderId);
            setFinalStatus('cancelled');
          } else if (collectionStatus === 'approved' || urlStatus === 'approved') {
            // Try to verify and process the payment
            const verifyResponse = await ticketsApi.verifyPayment(orderId, paymentId);
            setVerificationStatus(verifyResponse.data);
            setFinalStatus(verifyResponse.data.status === 'approved' ? 'success' : verifyResponse.data.status);
          } else if (collectionStatus === 'rejected' || urlStatus === 'rejected') {
            setFinalStatus('failure');
          } else if (collectionStatus === 'pending' || collectionStatus === 'in_process') {
            setFinalStatus('pending');
          } else {
            // Default: try to verify anyway
            try {
              const verifyResponse = await ticketsApi.verifyPayment(orderId, paymentId);
              setVerificationStatus(verifyResponse.data);
              if (verifyResponse.data.status === 'approved') {
                setFinalStatus('success');
              } else {
                setFinalStatus(verifyResponse.data.status || orderData.status);
              }
            } catch (e) {
              setFinalStatus(orderData.status);
            }
          }
          
          // Refresh order data
          const updatedOrder = await ticketsApi.getOrder(orderId);
          setOrder(updatedOrder.data);
          
        } catch (error) {
          console.error('Error verifying/fetching order:', error);
          setFinalStatus('error');
        }
      }
      setLoading(false);
    };
    // Small delay to let any pending webhooks process
    setTimeout(verifyAndFetchOrder, 500);
  }, [orderId, paymentId, collectionStatus, urlStatus]);

  const getStatusInfo = () => {
    switch (finalStatus) {
      case 'success':
      case 'approved':
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          title: '¡Compra Exitosa!',
          message: 'Tu pago ha sido procesado correctamente. Recibirás tus entradas por email.'
        };
      case 'failure':
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          title: 'Pago Rechazado',
          message: 'Hubo un problema con tu pago. Por favor, intenta nuevamente.'
        };
      case 'cancelled':
      case 'abandoned':
        return {
          icon: XCircle,
          color: 'text-orange-500',
          bgColor: 'bg-orange-500/10',
          title: 'Pago No Completado',
          message: 'El pago no fue completado. Podés intentar nuevamente cuando quieras.'
        };
      case 'pending':
      case 'in_process':
        return {
          icon: Clock,
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-500/10',
          title: 'Pago Pendiente',
          message: 'Tu pago está siendo procesado. Te notificaremos cuando se complete.'
        };
      default:
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          title: 'Orden Creada',
          message: 'Tu orden ha sido registrada correctamente.'
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <section className="pt-8 md:pt-32 pb-24 px-6 lg:px-12" data-testid="checkout-success-page">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div 
              className={`w-24 h-24 mx-auto mb-8 rounded-full ${statusInfo.bgColor} flex items-center justify-center`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <StatusIcon size={48} className={statusInfo.color} />
            </motion.div>
            
            <h1 className="text-4xl lg:text-5xl font-black tracking-tighter uppercase mb-4">
              {statusInfo.title}
            </h1>
            
            <p className="text-[#A1A1AA] text-lg mb-12 max-w-md mx-auto">
              {statusInfo.message}
            </p>

            {loading ? (
              <div className="animate-pulse bg-[#121212] border border-[#27272A] p-8">
                <div className="h-6 bg-[#1A1A1A] w-1/2 mx-auto mb-4" />
                <div className="h-4 bg-[#1A1A1A] w-3/4 mx-auto" />
              </div>
            ) : order && (
              <motion.div 
                className="bg-[#121212] border border-[#27272A] p-8 text-left mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-xl font-black uppercase tracking-tight mb-6">
                  Detalles de la Orden
                </h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-[#27272A] pb-4">
                    <span className="text-[#A1A1AA]">Número de orden</span>
                    <span className="font-mono">{order.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  {order.event_title && (
                    <div className="flex justify-between border-b border-[#27272A] pb-4">
                      <span className="text-[#A1A1AA]">Evento</span>
                      <span className="font-medium">{order.event_title}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-b border-[#27272A] pb-4">
                    <span className="text-[#A1A1AA]">Cantidad de entradas</span>
                    <span className="font-mono">{order.quantity}</span>
                  </div>
                  <div className="flex justify-between border-b border-[#27272A] pb-4">
                    <span className="text-[#A1A1AA]">Total pagado</span>
                    <span className="font-mono font-bold">${order.total_amount?.toLocaleString()}</span>
                  </div>
                  {paymentId && (
                    <div className="flex justify-between border-b border-[#27272A] pb-4">
                      <span className="text-[#A1A1AA]">ID de pago</span>
                      <span className="font-mono text-sm">{paymentId}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Estado</span>
                    <span className={`font-mono uppercase ${
                      order.status === 'approved' ? 'text-green-500' : 
                      order.status === 'pending' ? 'text-yellow-500' : 'text-[#A1A1AA]'
                    }`}>
                      {order.status === 'approved' ? 'Aprobado' : 
                       order.status === 'pending' ? 'Pendiente' : order.status}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {(status === 'success' || status === 'approved') && (
              <motion.p 
                className="text-[#A1A1AA] mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Recibirás un email con tus entradas y códigos QR.
                También puedes ver tus tickets en "Mi Cuenta".
              </motion.p>
            )}

            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {(status === 'success' || status === 'approved') && (
                <Link to="/mi-cuenta" className="btn-primary inline-flex items-center justify-center group">
                  <Ticket size={18} className="mr-2" />
                  Ver Mis Tickets
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
              <Link to="/eventos" className="btn-secondary inline-flex items-center justify-center">
                {status === 'failure' || status === 'rejected' ? 'Intentar de nuevo' : 'Ver más eventos'}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
